import React, { useEffect, useRef } from 'react';
import { Viewport } from '../WorldCanvas/WorldCanvas';

interface GridOverlayProps {
  viewport   : Viewport;
  width      : number;
  height     : number;
  visible    : boolean;
  brightness?: number;   // multiplier on line opacity (1 = default), for low-contrast screens
}

/**
 * Draws a coordinate grid (world-aligned) over the game canvas.
 *
 * Screen ↔ world mapping (Y-up, matching useWorldRenderer):
 *   screen_x =  (world_x − vp.x) × zoom + w/2
 *   screen_y = −(world_y − vp.y) × zoom + h/2
 *
 * The grid step adapts to the current zoom level so the lines are
 * never too dense or too sparse.
 */
export const GridOverlay: React.FC<GridOverlayProps> = ({
  viewport, width, height, visible, brightness = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);
    if (!visible) return;

    // Scale every line's alpha by the brightness multiplier (clamped to 1)
    // so faint grids stay readable on low-contrast phone screens.
    const a = (base: number) => Math.min(1, base * brightness).toFixed(3);

    const { x: vpX, y: vpY, zoom } = viewport;

    // ── Choose a world-unit step that gives ~40-120 px between lines ──
    const targetPx = 60;                     // ideal pixel spacing
    const rawStep  = targetPx / zoom;        // world units per gap (raw)

    // Round to nearest "nice" step: 1, 2, 5, 10, 20, 50, 100, …
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const fraction  = rawStep / magnitude;
    let step: number;
    if      (fraction < 1.5) step = magnitude;
    else if (fraction < 3.5) step = magnitude * 2;
    else if (fraction < 7.5) step = magnitude * 5;
    else                     step = magnitude * 10;

    // Minor lines must be at least 1 screen pixel apart
    step = Math.max(step, 1 / zoom);

    // One coarser major-grid level (×10)
    const majorStep = step * 10;

    // ── World-to-screen helpers ────────────────────────────────────
    const toScreenX = (wx: number) =>  (wx - vpX) * zoom + width  / 2;
    const toScreenY = (wy: number) => -(wy - vpY) * zoom + height / 2;

    // ── Visible world range ────────────────────────────────────────
    const worldLeft   =  vpX - width  / 2 / zoom;
    const worldRight  =  vpX + width  / 2 / zoom;
    const worldBottom =  vpY - height / 2 / zoom;
    const worldTop    =  vpY + height / 2 / zoom;

    const firstCol = Math.floor(worldLeft   / step) * step;
    const firstRow = Math.floor(worldBottom / step) * step;

    // ── Draw minor lines ───────────────────────────────────────────
    ctx.lineWidth   = 0.5;
    ctx.strokeStyle = `rgba(255,255,255,${a(0.08)})`;
    ctx.beginPath();

    for (let wx = firstCol; wx <= worldRight; wx += step) {
      if (wx % majorStep === 0) continue;           // skip — drawn as major
      const sx = toScreenX(wx);
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, height);
    }
    for (let wy = firstRow; wy <= worldTop; wy += step) {
      if (wy % majorStep === 0) continue;
      const sy = toScreenY(wy);
      ctx.moveTo(0, sy);
      ctx.lineTo(width, sy);
    }
    ctx.stroke();

    // ── Draw major lines ───────────────────────────────────────────
    ctx.lineWidth   = 1;
    ctx.strokeStyle = `rgba(255,255,255,${a(0.18)})`;
    ctx.beginPath();

    const firstMajorCol = Math.floor(worldLeft   / majorStep) * majorStep;
    const firstMajorRow = Math.floor(worldBottom / majorStep) * majorStep;

    for (let wx = firstMajorCol; wx <= worldRight; wx += majorStep) {
      const sx = toScreenX(wx);
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, height);
    }
    for (let wy = firstMajorRow; wy <= worldTop; wy += majorStep) {
      const sy = toScreenY(wy);
      ctx.moveTo(0, sy);
      ctx.lineTo(width, sy);
    }
    ctx.stroke();

    // ── Axis lines (x=0, y=0) ─────────────────────────────────────
    ctx.lineWidth   = 1.5;
    ctx.strokeStyle = `rgba(255,255,255,${a(0.35)})`;
    ctx.beginPath();
    const originX = toScreenX(0);
    const originY = toScreenY(0);
    if (originX >= 0 && originX <= width) {
      ctx.moveTo(originX, 0);
      ctx.lineTo(originX, height);
    }
    if (originY >= 0 && originY <= height) {
      ctx.moveTo(0, originY);
      ctx.lineTo(width, originY);
    }
    ctx.stroke();

    // ── Coordinate labels at grid nodes ───────────────────────────
    {
      const fontSize = Math.max(8, Math.min(11, zoom * 1.4));
      ctx.font         = `${fontSize}px 'Courier New', monospace`;
      ctx.textBaseline = 'top';
      ctx.textAlign    = 'left';

      const pad = 3; // offset from the node point

      for (let wx = firstMajorCol; wx <= worldRight; wx += majorStep) {
        const sx = toScreenX(wx);
        if (sx < 0 || sx > width) continue;

        for (let wy = firstMajorRow; wy <= worldTop; wy += majorStep) {
          const sy = toScreenY(wy);
          if (sy < 0 || sy > height) continue;

          const label = `${wx},${wy}`;

          // Dark shadow for readability
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.fillText(label, sx + pad + 1, sy + pad + 1);

          ctx.fillStyle = `rgba(255,255,255,${a(0.40)})`;
          ctx.fillText(label, sx + pad, sy + pad);
        }
      }
    }

  }, [viewport, width, height, visible, brightness]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position      : 'absolute',
        top           : 0,
        left          : 0,
        pointerEvents : 'none',
        zIndex        : 5,
        display       : visible ? 'block' : 'none',
      }}
    />
  );
};